import React from 'react'
import { ComponentConfig } from '@puckeditor/core'
import { BandSlider } from 'components/BandSlider'
import { getPuckFileUrl, UploadedFile } from '../../fields/FileUploadField'
import type { ThemeCustomFields } from '../../theme-config'
import { withLayout } from '../Layout'

export type BandSliderProps = {
  images: Array<{ image: UploadedFile | string }>
  speed?: string
  gap?: string
  imageHeight?: string
  imageMargin?: string
  background?: string
  direction?: 'left' | 'right'
  repeat?: number
}

// withLayout adds padding/margin/size + background/border on a wrapper (BR-5406).
export const PuckBandSlider = withLayout<ComponentConfig<{
  props: BandSliderProps
  fields: ThemeCustomFields
}>>({
  label: 'Band Slider',
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description: 'Endlessly scrolling strip of images, such as brand logos.',
    fields: {
      speed: 'Duration of one loop, e.g. 60s; smaller is faster.',
      repeat: 'Times the set repeats, to fill wide screens.',
    },
  },
  defaultProps: {
    images: [],
    speed: '120s',
    gap: '20px',
    // Everything below is optional and empty by default, so an existing
    // Band Slider keeps rendering exactly as before.
    imageHeight: '',
    imageMargin: '',
    background: '',
    direction: 'left',
    repeat: 1,
  },
  fields: {
    images: {
      type: 'array',
      label: 'Images',
      arrayFields: {
        image: { type: 'fileUpload', label: 'Image' },
      },
      getItemSummary: (_item, index) => `Image #${(index ?? 0) + 1}`,
    },
    speed: { type: 'text', label: 'Speed (e.g. 120s, 60s)' },
    gap: { type: 'text', label: 'Gap (e.g. 20px)' },
    imageHeight: {
      type: 'text',
      label: 'Image Height (blank keeps the original size)',
      placeholder: '142px',
    },
    imageMargin: {
      type: 'text',
      label: 'Image Margin (blank keeps 20px 10px)',
      placeholder: '0',
    },
    background: {
      type: 'colorPicker',
      label: 'Band Background',
    },
    direction: {
      type: 'radio',
      label: 'Direction',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
      ],
    },
    repeat: {
      type: 'number',
      label: 'Repeat Set',
      min: 1,
      max: 20,
    },
  },
  render: ({
    images,
    speed,
    gap,
    imageHeight,
    imageMargin,
    background,
    direction,
    repeat,
  }) => {
    const urls = (images ?? [])
      .map(({ image }) =>
        typeof image === 'string' ? image : getPuckFileUrl(image),
      )
      .filter(Boolean)

    if (urls.length === 0) {
      return (
        <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
          No images added
        </div>
      )
    }

    return (
      <BandSlider
        images={urls}
        speed={speed}
        gap={gap}
        imageHeight={imageHeight}
        imageMargin={imageMargin}
        background={background}
        direction={direction}
        repeat={repeat}
      />
    )
  },
})
